import { TabPanel } from "@mui/lab";
import { Box, darken, lighten, List, Skeleton, Stack, Typography as T } from "@mui/material";
import useDomains from "cohorts/hooks/useDomains";
import AccessLevelChip from "datasets/components/AccessLevelChip";
import DatasetPageLayout from "datasets/components/DatasetPageLayout";
import useDataset from "datasets/hooks/useDataset";
import useDatasetPrefixes from "datasets/hooks/useDatasetPrefixes";
import useIsMasterDataset from "datasets/hooks/useIsMasterDataset";
import usePreviewMode from "datasets/hooks/usePreviewMode";
import DescriptionEditor from "datasets/pages/overview/components/DescriptionEditor";
import NavigationCard from "datasets/pages/overview/components/NavigationCard";
import OverviewStats from "datasets/pages/overview/components/OverviewStats";
import { DatasetAccessLevel } from "datasets/types/enums/access-level";
import Dataset from "datasets/types/interfaces/dataset";
import { getAccentColor } from "datasets/utils/color";
import pluralize from "pluralize";
import yn from "yn";
import { CohortIcon, FilesToolIcon, ReportsIcon, TablesIcon } from "~/assets/icons";
import EditableText from "~/components/EditableText";
import useErrorToast from "~/contexts/error-toast";
import useUserPermission from "~/hooks/useUserPermission";
import rumClient, { trackEvent } from "~/utils/monitoring";

const {
  VITE_CATALOG_API,
  VITE_COHORT_EXPLORER_ENABLED,
  VITE_DATASET_FILES_ENABLED,
  VITE_DATASET_PERMISSIONS_ENABLED,
} = import.meta.env;

const Skeletons = () => (
  <Stack gap={1}>
    {Array.from({ length: 6 }).map((_, i) => (
      <Skeleton key={i} variant="text" height={35} />
    ))}
  </Stack>
);

const DatasetOverview = () => {
  const { showError } = useErrorToast();
  const isMasterDataset = useIsMasterDataset();
  const { data: dataset, isLoading: loadingDataset, mutate } = useDataset();
  const { data: prefixes, isLoading: loadingPrefixes } = useDatasetPrefixes();
  const { data: domains } = useDomains();
  const overviewDetailsWidth = 440;
  const hasEditPermission = useUserPermission("edit:catalog-dataset");
  const canEditDataset = yn(VITE_DATASET_PERMISSIONS_ENABLED)
    ? dataset?.access_level === DatasetAccessLevel.OWNER && hasEditPermission
    : hasEditPermission;
  const isPreviewUser = yn(VITE_DATASET_PERMISSIONS_ENABLED)
    ? dataset?.access_level === DatasetAccessLevel.PREVIEW
    : false;
  const canViewCohortExplorer = useUserPermission("view:cohort-explorer-app");
  const cohortExplorerEnabled =
    canViewCohortExplorer && yn(VITE_COHORT_EXPLORER_ENABLED) && !!domains?.length;
  const isPreviewModeEnabled = usePreviewMode();

  const updateTitle = async (title: string) => {
    await mutate(
      async () => {
        try {
          const resp = await fetch(`${VITE_CATALOG_API}/datasets/${dataset?.id}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ title }),
          });

          if (!resp.ok) {
            throw new Error("Unable to update dataset title");
          }

          const datasetResp: Dataset = await resp.json();

          trackEvent("dataset_title_updated", {
            dataset_id: datasetResp.id,
            dataset_title: datasetResp.title,
          });

          return datasetResp;
        } catch (e: any) {
          console.error(e);
          rumClient?.recordError(e);
          showError(e.message);
        }
      },
      { optimisticData: (data) => ({ ...data!, title }) }
    );
  };

  return (
    <TabPanel value="overview" sx={{ p: 0 }}>
      <DatasetPageLayout>
        <Box pt={9}>
          <Box display="flex" gap={8}>
            <Stack flexGrow={1} maxWidth={`calc(100% - ${overviewDetailsWidth}px)`} gap={2}>
              {loadingDataset && (
                <>
                  <Skeleton variant="rounded" height="40px" width="200px" />
                  <Skeletons />
                </>
              )}
              {dataset && (
                <>
                  <Stack alignItems="flex-start" gap={1}>
                    {yn(VITE_DATASET_PERMISSIONS_ENABLED) && (
                      <AccessLevelChip
                        showTooltip
                        accessLevel={dataset.access_level}
                        isPreviewModeEnabled={isPreviewModeEnabled}
                        sx={{
                          color: darken(getAccentColor(dataset.accentColor), 0.2),
                          backgroundColor: lighten(getAccentColor(dataset.accentColor), 0.92),
                        }}
                        tooltipPlacement="top"
                      />
                    )}
                    <T
                      component="h1"
                      fontSize={40}
                      fontWeight={700}
                      color={getAccentColor(dataset.accentColor)}
                      lineHeight={1}
                      textAlign="start"
                    >
                      {canEditDataset ? (
                        <EditableText
                          value={dataset.title}
                          placeholder="Untitled"
                          typographySx={{
                            color: "inherit",
                            fontSize: "inherit",
                            fontWeight: "inherit",
                            lineHeight: "inherit",
                            textAlign: "inherit",
                          }}
                          handleBlur={(val) => {
                            if (val !== dataset.title) {
                              updateTitle(val);
                            }
                          }}
                        />
                      ) : (
                        dataset.title
                      )}
                    </T>
                  </Stack>

                  <DescriptionEditor dataset={dataset} canEdit={canEditDataset} />
                </>
              )}
            </Stack>
            <Stack flex={`0 0 ${overviewDetailsWidth}px`} gap={2} alignItems="center">
              <Stack sx={{ gap: 2, position: "sticky", top: 24, width: "100%" }}>
                <OverviewStats />

                <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <NavigationCard
                    title="Dashboards"
                    icon={<ReportsIcon />}
                    description={
                      dataset?.report_count === 0
                        ? "No dashboards available."
                        : "Explore with curated dashboards."
                    }
                    url={`/data-browser/datasets/${dataset?.id}/reports`}
                    loading={loadingDataset}
                  />
                  {!isPreviewUser && (
                    <NavigationCard
                      title="Tables"
                      icon={<TablesIcon />}
                      description={
                        dataset?.table_count === 0
                          ? "No tables available."
                          : `${pluralize("tables", dataset?.table_count, true)} available.`
                      }
                      url={`/data-browser/datasets/${dataset?.id}/tables`}
                      loading={loadingDataset}
                    />
                  )}
                  {!isPreviewUser && yn(VITE_DATASET_FILES_ENABLED) && !isMasterDataset && (
                    <NavigationCard
                      title="Files"
                      icon={<FilesToolIcon />}
                      description={
                        prefixes?.length === 0
                          ? "No folders available."
                          : `${pluralize("folders", prefixes?.length, true)} available.`
                      }
                      url={`/data-browser/datasets/${dataset?.id}/files`}
                      loading={loadingPrefixes}
                    />
                  )}

                  {cohortExplorerEnabled && (
                    <NavigationCard
                      title="Build a Cohort"
                      icon={<CohortIcon />}
                      description="Create a participant group for analysis."
                      url="/cohorts/new"
                      loading={loadingDataset}
                      state={{ datasetId: dataset?.id }}
                    />
                  )}
                </List>
              </Stack>
            </Stack>
          </Box>
        </Box>
      </DatasetPageLayout>
    </TabPanel>
  );
};

export default DatasetOverview;
